import { Controller } from "@hotwired/stimulus"

// The Edit page's banner after the browser extension brought in Mezco's
// product page: while the pictures are being sorted (SortImportedPicturesJob)
// it asks how that's going, then puts the sorted pictures in place.
//
// Meanwhile the banner stays in view as the page scrolls, the Pictures
// section is covered (no dragging, deleting or dropping in), and the form
// can't be saved, so nothing is moved or saved under the sort. If it fails
// or gets stuck, everything opens up again and Sort again starts it over.
export default class extends Controller {
  static targets = ["status", "retry"]
  static values = { url: String, sortUrl: String, sorting: Boolean }

  connect() {
    this.holdSubmit = (event) => { if (this.sortingValue) event.preventDefault() }
    this.form?.addEventListener("submit", this.holdSubmit, true)
    if (this.sortingValue) this.start()
  }

  disconnect() {
    clearInterval(this.timer)
    this.form?.removeEventListener("submit", this.holdSubmit, true)
    this.lock(false)
  }

  get form() {
    return document.getElementById("figure-form")
  }

  start() {
    this.sortingValue = true
    this.lock(true)
    clearInterval(this.timer)
    this.timer = setInterval(() => this.check(), 3000)
  }

  async check() {
    const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
    if (!response.ok) return
    const { status, note, stuck, html } = await response.json()
    if (status === "sorting" && !stuck) return
    clearInterval(this.timer)
    this.sortingValue = false
    this.lock(false)
    if (stuck) return this.failed("Sorting the pictures seems to have stopped, so they're as they were.")
    const list = document.getElementById("sortable-images")
    if (list && html) list.innerHTML = html
    if (status === "unsorted") return this.failed(note)
    this.statusTarget.textContent = note || "Pictures sorted. Drag any that need it."
  }

  failed(note) {
    this.statusTarget.textContent = note
    this.retryTarget.hidden = false
  }

  async retry() {
    this.retryTarget.disabled = true
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.sortUrlValue, { method: "POST", headers: { Accept: "application/json", "X-CSRF-Token": token } })
      if (!response.ok) throw new Error(response.status)
      this.retryTarget.hidden = true
      this.statusTarget.innerHTML = '<i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Sorting the pictures again…'
      this.start()
    } catch (e) {
      this.statusTarget.textContent = "Couldn't start sorting again. Reload the page and try once more."
    } finally {
      this.retryTarget.disabled = false
    }
  }

  // Sorting: the banner sticks, the pictures are covered, Save is off.
  lock(on) {
    this.element.classList.toggle("is-sorting", on)
    // How far the side menu, also sticky, keeps below the banner.
    document.documentElement.style.setProperty("--fe-banner-h", `${on ? this.element.offsetHeight : 0}px`)
    document.getElementById("pictures")?.classList.toggle("is-sorting", on)
    document.querySelectorAll('#figure-form [type="submit"], [form="figure-form"][type="submit"]').forEach((button) => {
      button.disabled = on
      button.title = on ? "Waiting for the pictures to be sorted" : ""
    })
  }
}
